import { FlagCountryCode } from '@opaz/ui-components';
import type { Feature, FeatureCollection, Polygon, GeoJsonProperties } from 'geojson';

export type CountryIso2MobileCode = {
  mobileCode: string;
  countryIso2: FlagCountryCode;
};

export interface ServicesCategoriesCard {
  id: number;
  documentId: string;
  title: string;
  description: string;
  services: Service[];
}

export interface ZoneItem {
  id: string;
  documentId: string;
  title: string;
}

export interface ServiceStep {
  id: number;
  step: string;
}

export interface ServiceAuthority {
  id: number;
  documentId: string;
  title: string;
  description?: string;
}
export interface Service {
  id: number;
  documentId: string;
  title: string;
  code: string;
  url: string;
  description: string;
  duration: string;
  cost: string;
  steps: ServiceStep[];
  authority: ServiceAuthority;
  beforeYouStart2: ServiceStep[];
  OutputOfServices?: ServiceStep[];
  locale: string;
  logo?: {
    url: string;
  };
  zones: { documentId: string; title: string; code: string }[];
  localizations?: Array<{
    title: string;
    locale: string;
  }>;
}

export interface Faq {
  documentId: string;
  question: string;
  answer: string;
  category: string;
  displayOrder: number;
  isFeatured: boolean;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  locale: string;
  localizations?: Array<{
    question: string;
    locale: string;
  }>;
}

export interface AboutUsData {
  id: number;
  documentId: string;
  Title: string;
  bodyContent: string;
  subTitle: string;
  lastTitle: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  locale: string;
  subSection: Array<{
    id: number;
    number: string;
    unit: string;
    shortText: string;
    description: string;
    displayOrder: number;
  }>;
  video: {
    id: number;
    documentId: string;
    name: string;
    alternativeText: string;
    caption: string;
    width: number;
    height: number;
    formats: string;
    hash: string;
    ext: string;
    mime: string;
    size: number;
    url: string;
    previewUrl: string;
    provider: string;
    provider_metadata: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
  };
  localizations?: {
    data: Array<{
      id: number;
      attributes: {
        locale: string;
      };
    }>;
  };
  homePageImage?: {
    url: string;
  };
}

export type SubSection = {
  id: number;
  number: string;
  unit: string;
  shortText: string;
  description: string;
  displayOrder: number;
};

export type FooterLinkProps = {
  to?: string;
  external?: boolean;
  openInNewTab?: boolean;
};

export const EVENT_TYPE = {
  Run: 'run',
  Content: 'content',
  Error: 'error',
  Tool: 'tool',
  Postprocessing: 'postprocessing',
  Followup: 'followup',
} as const;

export type EventType = (typeof EVENT_TYPE)[keyof typeof EVENT_TYPE];

export const RUN_STATUS = {
  Started: 'started',
  Completed: 'completed',
  Canceled: 'canceled',
  Paused: 'paused',
  Continued: 'continued',
} as const;

export type RunStatus = (typeof RUN_STATUS)[keyof typeof RUN_STATUS];

export const TOOL_STATUS = {
  Started: 'started',
  Completed: 'completed',
} as const;

export type PostprocessingContent = {
  zone_detected: boolean;
  zone_code?: string[];
  plot_detected?: boolean;
};
export type ToolStatus = (typeof TOOL_STATUS)[keyof typeof TOOL_STATUS];

export type StreamEvent =
  | { type: typeof EVENT_TYPE.Run; status: RunStatus }
  | { type: typeof EVENT_TYPE.Content; content: string }
  | { type: typeof EVENT_TYPE.Error; content: string }
  | {
      type: typeof EVENT_TYPE.Tool;
      status: ToolStatus;
      name: string;
      arguments?: unknown;
      result?: unknown;
    }
  | {
      type: typeof EVENT_TYPE.Postprocessing;
      content: PostprocessingContent;
    }
  | {
      type: typeof EVENT_TYPE.Followup;
      index: number;
      content: string;
    }
  | Record<string, unknown>;

export const CONTENT_KIND = {
  Text: 'text',
  Error: 'error',
  Tool: 'tool',
  Postprocessing: 'postprocessing',
} as const;

export type ContentKind = (typeof CONTENT_KIND)[keyof typeof CONTENT_KIND];

export type ChatMessage = {
  type: 'user' | 'bot';
  content: string;
  isStreaming?: boolean;
  kind?: ContentKind;
};

export type GeoGeometry = Polygon;

export type ZoneProperties = {
  code: string;
  ZoneName?: string;
  ZoneNameAr?: string;
  ZoneNameEn?: string;
  ZoneDesrip?: string;
  ZoneDesripAr?: string;
  ZoneDesripEn?: string;
} & GeoJsonProperties;

export type PlotProperties = {
  id?: string | number;
  pkPlotID?: string | number;
  EnglishName?: string;
  ArabicName?: string;
  SHAPE_Area?: number;
  Shape_Area?: number;
  Shape__Area?: number;
  rent?: Record<string, number>;
} & GeoJsonProperties;

export type ZoneFeature = Feature<GeoGeometry, ZoneProperties>;
export type PlotFeature = Feature<GeoGeometry, PlotProperties>;
export type ZoneFeatureCollection = FeatureCollection<GeoGeometry, ZoneProperties>;
export type PlotFeatureCollection = FeatureCollection<GeoGeometry, PlotProperties>;
